/**
 * External store for live decal feature preview during slider/pad drags.
 * Covers displayCutout, cameraLens, and homeIndicator features.
 * Keyed by feature ID. Bypasses Redux so undo history is not polluted.
 *
 * Uses merge semantics: setPreviewDecalFeature merges incoming changes
 * with the existing preview for that ID.
 */

export interface PreviewDisplayCutout {
  type: 'displayCutout'
  xOffset: number
  yOffset: number
  width: number
  height: number
  cornerRadius: number
}

export interface PreviewCameraLens {
  type: 'cameraLens'
  xOffset: number
  yOffset: number
  radius: number
}

export interface PreviewHomeIndicator {
  type: 'homeIndicator'
  yOffset: number
  width: number
  height: number
  cornerRadius: number
  dark: boolean
}

export type PreviewDecalFeature = PreviewDisplayCutout | PreviewCameraLens | PreviewHomeIndicator

let snapshot: Record<string, PreviewDecalFeature> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

/**
 * Set (merge) preview for a specific decal feature ID.
 * @param id — feature ID
 * @param base — full fallback (from Redux) used when no preview record exists yet
 * @param changes — partial overrides to merge on top
 */
export const setPreviewDecalFeature = <T extends PreviewDecalFeature>(id: string, base: T, changes: Partial<T>) => {
  const existing = snapshot?.[id] as T | undefined
  snapshot = {
    ...snapshot,
    [id]: {
      ...base,
      ...existing,
      ...changes,
    },
  }
  notify()
}

export const clearPreviewDecalFeature = () => {
  snapshot = null
  notify()
}

export const getPreviewDecalFeatureSnapshot = (): Record<string, PreviewDecalFeature> | null => snapshot

export const subscribePreviewDecalFeature = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
